import * as React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

/**
 * Standalone error block matching the destructive-box style already used
 * ad hoc across dashboard.page.tsx, transactions.page.tsx, and DataTable.
 */
export function ErrorState({
  message = "Something went wrong",
  onRetry,
  retryLabel = "Try again",
  className,
}: ErrorStateProps) {
  return (
    <div
      className={cn(
        "rounded-md border border-destructive/50 bg-destructive/10 px-4 py-8 text-center",
        className
      )}
    >
      <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-destructive/15 text-destructive">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium text-destructive">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export interface TableErrorRowProps {
  colSpan: number;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

/** Error state rendered as a single table row, for tables that keep their header visible on error. */
export function TableErrorRow({
  colSpan,
  message,
  onRetry,
  retryLabel,
  className,
}: TableErrorRowProps) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className={cn("p-0", className)}>
        <ErrorState
          message={message}
          onRetry={onRetry}
          retryLabel={retryLabel}
          className="rounded-none border-0"
        />
      </TableCell>
    </TableRow>
  );
}
